import { useState, useRef, useCallback, useEffect } from 'react'

export type AudioRecordingUnavailableReason =
  | 'permission-denied'
  | 'device-not-found'
  | 'device-busy'
  | 'unsupported'
  | 'unknown'

interface UseAudioRecorderReturn {
  isRecording: boolean
  elapsedSec: number
  audioBlob: Blob | null
  audioUrl: string | null
  startRecording: () => Promise<boolean>
  stopRecording: () => void
  resetRecording: () => void
  error: string | null
  unavailableReason: AudioRecordingUnavailableReason | null
}

function classifyUnavailableReason(error: unknown): AudioRecordingUnavailableReason {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
      return 'permission-denied'
    }
    if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      return 'device-not-found'
    }
    if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
      return 'device-busy'
    }
    if (error.name === 'NotSupportedError') return 'unsupported'
  }
  if (error instanceof TypeError || error instanceof ReferenceError) return 'unsupported'
  return 'unknown'
}

export function useAudioRecorder(): UseAudioRecorderReturn {
  const [isRecording, setIsRecording] = useState(false)
  const [elapsedSec, setElapsedSec] = useState(0)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [unavailableReason, setUnavailableReason] =
    useState<AudioRecordingUnavailableReason | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (audioUrl) URL.revokeObjectURL(audioUrl)
      streamRef.current?.getTracks().forEach(t => t.stop())
    }
  }, [audioUrl])

  const startRecording = useCallback(async () => {
    try {
      setError(null)
      setUnavailableReason(null)
      setAudioBlob(null)
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl)
        setAudioUrl(null)
      }

      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
        throw new DOMException('Audio recording is unsupported', 'NotSupportedError')
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm'

      const recorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = recorder
      chunksRef.current = []

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType })
        setAudioBlob(blob)
        setAudioUrl(URL.createObjectURL(blob))
        stream.getTracks().forEach(t => t.stop())
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
      }

      recorder.start(1000)
      setIsRecording(true)
      setElapsedSec(0)

      timerRef.current = setInterval(() => {
        setElapsedSec(prev => prev + 1)
      }, 1000)
      return true
    } catch (e) {
      streamRef.current?.getTracks().forEach(track => track.stop())
      streamRef.current = null
      const reason = classifyUnavailableReason(e)
      const msg = e instanceof DOMException && e.name === 'NotAllowedError'
        ? '마이크 접근이 거부되었습니다. 브라우저 설정에서 마이크를 허용해주세요.'
        : '녹음을 시작할 수 없습니다.'
      setError(msg)
      setUnavailableReason(reason)
      return false
    }
  }, [audioUrl])

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop()
    }
    setIsRecording(false)
  }, [])

  const resetRecording = useCallback(() => {
    setAudioBlob(null)
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl)
      setAudioUrl(null)
    }
    setElapsedSec(0)
    setError(null)
    setUnavailableReason(null)
  }, [audioUrl])

  return {
    isRecording,
    elapsedSec,
    audioBlob,
    audioUrl,
    startRecording,
    stopRecording,
    resetRecording,
    error,
    unavailableReason,
  }
}
